/**
 * Pendo Analytics Service
 *
 * This service handles the integration with Pendo for user analytics and feedback.
 * It initializes Pendo with user and account information from the authentication profile.
 */

class PendoService {
    constructor() {
        console.log("Pendo Service constructor");
        this.isInitialized = false;
        // Get API key from environment variable
        // The client will set this value in their .env file
        this.apiKey = "85e435f3-3c45-49c5-909c-7902b79946a6";
    }

    /**
     * Get the singleton instance of PendoService
     */
    static getInstance() {
        if (!PendoService.instance) {
            PendoService.instance = new PendoService();
        }
        return PendoService.instance;
    }

    /**
     * Load the Pendo script dynamically
     */
    loadPendoScript() {
        return new Promise((resolve, reject) => {
            console.log("loadPendoScript method");
            console.log(process.env);
            // Don't load if already loaded or no API key
            if (window.pendo || !this.apiKey) {
                resolve();
                return;
            }

            // Create the pendo initialization function
            const initScript = document.createElement('script');
            initScript.type = 'text/javascript';
            initScript.innerHTML = `
        (function(apiKey){
          (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];
          v=['initialize','identify','updateOptions','pageLoad','track','trackAgent'];for(w=0,x=v.length;w<x;++w)(function(m){
            o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
            y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js';
            z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');
        })('${this.apiKey}');
      `;

            document.head.appendChild(initScript);

            // Wait for Pendo to be available
            const checkPendo = setInterval(() => {
                if (window.pendo && window.pendo.initialize) {
                    clearInterval(checkPendo);
                    resolve();
                }
            }, 100);

            // Timeout after 10 seconds
            setTimeout(() => {
                clearInterval(checkPendo);
                if (!window.pendo) {
                    reject(new Error('Pendo script failed to load'));
                }
            }, 10000);
        });
    }

    /**
     * Initialize Pendo with user and account data
     * @param {Object|null} userProfile - User profile object from Auth
     */
    async initialize(userProfile) {
        try {
            // Skip if no API key is configured
            if (!this.apiKey) {
                console.warn('Pendo API key not configured. Skipping Pendo initialization.');
                return;
            }

            // Skip if already initialized
            if (this.isInitialized) {
                console.log('Pendo already initialized');
                return;
            }

            // Load Pendo script
            await this.loadPendoScript();

            // Prepare visitor and account data
            const options = this.preparePendoOptions(userProfile);

            // Initialize Pendo
            if (window.pendo && window.pendo.initialize) {
                window.pendo.initialize(options);
                this.isInitialized = true;
                console.log('Pendo initialized successfully');
            }
        } catch (error) {
            console.error('Error initializing Pendo:', error);
        }
    }

    /**
     * Prepare Pendo options from user profile
     * @param {Object|null} userProfile - User profile object
     * @returns {Object} Pendo options object
     */
    preparePendoOptions(userProfile) {
        console.log("preparePendoOptions");
        console.log(userProfile);
        const visitor = {
            id: userProfile?.sub || userProfile?.name || 'anonymous-user',
        };

        // Add optional visitor fields if available
        if (userProfile?.email) {
            visitor.email = userProfile.email;
        }
        if (userProfile?.name) {
            visitor.full_name = userProfile.name;
        }
        if (userProfile?.scope && userProfile.scope.length > 0) {
            visitor.role = this.extractRole(userProfile.scope);
        }

        const account = {
            id: userProfile?.tenantId || userProfile?.sub || 'default-account',
        };

        // Add optional account fields if available
        if (userProfile?.tenantName) {
            account.name = userProfile.tenantName;
        }

        return {
            visitor,
            account,
        };
    }

    /**
     * Extract user role from scopes
     * @param {Array<string>} scopes - Array of user scopes
     * @returns {string} User role
     */
    extractRole(scopes) {
        // Check for admin roles
        if (scopes.some(scope => scope.toLowerCase().includes('admin'))) {
            return 'admin';
        }
        // Check for other common roles
        if (scopes.some(scope => scope.toLowerCase().includes('manager'))) {
            return 'manager';
        }
        return 'user';
    }

    /**
     * Update visitor information
     * @param {Object} updates - Partial visitor updates
     */
    updateVisitor(updates) {
        if (window.pendo && window.pendo.identify) {
            window.pendo.identify(updates);
        }
    }

    /**
     * Track a custom event
     * @param {string} eventName - Name of the event to track
     * @param {Object} metadata - Optional metadata for the event
     */
    track(eventName, metadata) {
        if (window.pendo && window.pendo.track) {
            window.pendo.track(eventName, metadata);
        }
    }

    /**
     * Manually trigger a page load event
     */
    pageLoad() {
        if (window.pendo && window.pendo.pageLoad) {
            window.pendo.pageLoad();
        }
    }

    /**
     * Check if Pendo is initialized
     * @returns {boolean} True if Pendo is ready
     */
    isReady() {
        return this.isInitialized && !!window.pendo;
    }
}

export default PendoService;